/* src/render/interiorGeometry.ts — pure footprint geometry for the walkable interior.
 *
 * Split out of interiorView.ts so the room-shaping maths — is this point inside the footprint,
 * how far is it from the nearest wall, may the player stand here, which way does W move — can be
 * unit-tested without a WebGL context (CLAUDE.md Testing). interiorView.ts owns the Three.js
 * scene and the input; this file owns the arithmetic, and nothing here touches the GPU.
 */

export type Poly = ReadonlyArray<readonly [number, number]>;

export interface Bounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
}

/**
 * Even-odd ray cast: is (px, pz) inside the polygon? Robust for the concave, L-shaped footprints
 * OSM hands us, where a centroid-based test would be wrong.
 */
export function pointInPoly(px: number, pz: number, poly: Poly): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const a = poly[i];
    const b = poly[j];
    if (!a || !b) continue;
    const [xi, zi] = a;
    const [xj, zj] = b;
    if (zi > pz !== zj > pz && px < ((xj - xi) * (pz - zi)) / (zj - zi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** Shortest distance from a point to a segment, in the xz plane. */
export function distToSeg(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const dx = bx - ax;
  const dz = bz - az;
  const len2 = dx * dx + dz * dz;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / len2));
  const cx = ax + t * dx;
  const cz = az + t * dz;
  return Math.hypot(px - cx, pz - cz);
}

/** Distance from a point to the nearest wall. Cheap — footprints are a handful of edges. */
export function clearance(px: number, pz: number, poly: Poly): number {
  let min = Infinity;
  for (let i = 0; i < poly.length; i += 1) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    if (!a || !b) continue;
    min = Math.min(min, distToSeg(px, pz, a[0], a[1], b[0], b[1]));
  }
  return min;
}

/** May the player stand here — inside the room, and at least `radius` off every wall? */
export function standable(px: number, pz: number, poly: Poly, radius: number): boolean {
  return pointInPoly(px, pz, poly) && clearance(px, pz, poly) >= radius;
}

/** Axis-aligned bounds of a footprint. */
export function boundsOf(poly: Poly): Bounds {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of poly) {
    minX = Math.min(minX, p[0]);
    maxX = Math.max(maxX, p[0]);
    minZ = Math.min(minZ, p[1]);
    maxZ = Math.max(maxZ, p[1]);
  }
  return { minX, maxX, minZ, maxZ };
}

/** Eye-height y for a 0-based floor index. Storey f sits at f·floorHeight; add eye height. */
export function floorEyeY(floor: number, floorHeight: number, eye: number): number {
  return floor * floorHeight + eye;
}

/**
 * Yaw that aims the first-person forward vector from `(fromX,fromZ)` toward `(toX,toZ)`.
 * Forward is −Z rotated by yaw, so this is `atan2(-dx, -dz)`; degenerate input looks straight −Z.
 */
export function lookYaw(fromX: number, fromZ: number, toX: number, toZ: number): number {
  const dx = toX - fromX;
  const dz = toZ - fromZ;
  return dx === 0 && dz === 0 ? 0 : Math.atan2(-dx, -dz);
}

/** The stairwell's ground rectangle: foot at (x, z), climbing along (dx, dz) for `len` metres. */
export interface StairRegion {
  readonly x: number;
  readonly z: number;
  readonly dx: number;
  readonly dz: number;
  readonly len: number;
  readonly width: number;
}

/**
 * Where a standing position is along a flight of stairs: 0 at the foot, 1 at the head, or null
 * when the point is off the flight entirely. The end margins let a player step onto the flight
 * from slightly before its first tread without falling through the geometry; the sides are
 * exact, so walking past the staircase does not yank you onto it.
 */
export function stairProgress(
  px: number,
  pz: number,
  s: StairRegion,
  endMargin = 0.35,
): number | null {
  const relX = px - s.x;
  const relZ = pz - s.z;
  const along = relX * s.dx + relZ * s.dz;
  const across = relX * -s.dz + relZ * s.dx;
  if (Math.abs(across) > s.width / 2) return null;
  if (along < -endMargin || along > s.len + endMargin) return null;
  return Math.min(1, Math.max(0, along / s.len));
}

/**
 * Ground-plane move vector for the interior's WASD controller, in world (x, z) per unit input,
 * normalised so a diagonal is not faster than a straight line. Forward (into the screen) is −Z
 * rotated by yaw; right is +X rotated by yaw. Pure and exported so the sign convention is pinned
 * by a test rather than discovered by walking into a wall — the same guard camera.ts keeps on its
 * orbit-nudge vector.
 */
export function interiorMoveVector(forward: number, strafe: number, yaw: number): [number, number] {
  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  let mx = forward * -sin + strafe * cos;
  let mz = forward * -cos + strafe * -sin;
  const len = Math.hypot(mx, mz);
  if (len === 0) return [0, 0];
  mx /= len;
  mz /= len;
  return [mx, mz];
}
